import React, { useEffect, useMemo } from 'react';
import { Form, Formik } from 'formik';
import {
  Box,
  Button,
  Card,
  CircularProgress,
  LinearProgress,
  TextField,
  Typography
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import MailOutlineIcon from '@material-ui/icons/MailOutline';

import { FetchError } from '../../../common/api/api';
import { formatDate } from '../../../common/formatter';
import CardTitle from '../../../common/card/CardTitle';
import CardContent from '../../../common/card/CardContent';
import { TaskTypeEntity } from '../../task-types/TaskType.interface';
import { useTaskEntityFields } from '../../workflow/Workflow.hooks';
import { BaseSupplierCardProps, Supplier } from '../Supplier.interface';
import { useSupplierSendInvite, useUpdateSupplier } from '../Supplier.hooks';

const SupplierInviteCard: React.FC<BaseSupplierCardProps> = (
  props: BaseSupplierCardProps
) => {
  const { supplier, supplierVersion, onChange, onUpdate, onError } = props;
  const {
    updatedSupplier,
    isUpdating,
    updateError,
    updateSupplier
  } = useUpdateSupplier(supplier.id);
  const {
    invitedSupplier,
    isInviting,
    inviteError,
    inviteSupplier
  } = useSupplierSendInvite(supplier.id);
  const { isFieldDisabled } = useTaskEntityFields<TaskTypeEntity.Supplier>(
    supplier.task
  );
  const initialData = supplierVersion || supplier;
  const isInvited: boolean = !!supplier.sentDate;
  const isCompleted: boolean = !!supplier.completed;
  const error: FetchError | undefined = updateError || inviteError;

  const disabled: boolean = useMemo(() => {
    return !!supplierVersion || isFieldDisabled('email');
  }, [supplierVersion, isFieldDisabled]);

  useEffect(() => {
    if (updatedSupplier) {
      onChange?.(updatedSupplier);
      onUpdate?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedSupplier]);

  useEffect(() => {
    if (invitedSupplier) {
      onChange?.(invitedSupplier);
      onUpdate?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invitedSupplier]);

  useEffect(() => {
    if (error) {
      onError?.(error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  return (
    <Card>
      {isUpdating && <LinearProgress />}
      <CardTitle>
        <MailOutlineIcon />
        Supplier Invite
      </CardTitle>
      <CardContent padding='small'>
        <Typography display='block' variant='caption'>
          Invite the supplier to complete the evaluation form. The supplier will
          receive an email with a link.
        </Typography>
        <Box mt={2}>
          <Formik
            initialValues={
              { email: initialData.email || '' } as Partial<Supplier>
            }
            onSubmit={(values: Partial<Supplier>) => {
              // use null to clear email field
              const dto = {
                ...values,
                email: values.email || null
              } as Partial<Supplier>;
              updateSupplier(dto);
              onChange?.(dto);
            }}
          >
            {({ values, dirty, handleChange, handleBlur, submitForm }) => (
              <Form>
                <TextField
                  fullWidth
                  type='email'
                  name='email'
                  variant='outlined'
                  label='E-mail Address'
                  placeholder='Supplier E-mail'
                  value={values.email}
                  disabled={disabled || isInviting}
                  onChange={handleChange}
                  onBlur={event => {
                    handleBlur(event);
                    dirty && submitForm();
                  }}
                />
              </Form>
            )}
          </Formik>
        </Box>
        {isCompleted ? (
          <Box mt={2}>
            <Alert severity='success'>
              <span>
                Supplier Survey Completed on{' '}
                <strong>{formatDate(supplier.completed)}</strong>.
              </span>
            </Alert>
          </Box>
        ) : (
          isInvited && (
            <Box mt={2}>
              <Alert severity='success'>
                <span>
                  Supplier Invite Sent on{' '}
                  <strong>{formatDate(supplier.sentDate)}</strong>.
                </span>
              </Alert>
            </Box>
          )
        )}
        {!isCompleted && (
          <Box mt={2}>
            <Button
              color='primary'
              variant='outlined'
              onClick={() => inviteSupplier()}
              disabled={disabled || !supplier.email || isUpdating || isInviting}
              endIcon={isInviting && <CircularProgress size={20} />}
            >
              {isInvited ? 'Resend Invite' : 'Send Invite'}
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default SupplierInviteCard;
